import React, { useEffect, createElement } from 'react';
import { ArrowRightIcon, SearchIcon, FileTextIcon, ShareIcon, MailIcon, TrendingUpIcon, CheckCircleIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteMarketingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website marketing | Meer bezoekers & klanten | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Website marketing strategieën voor meer bezoekers en klanten. Van SEO en content marketing tot social media en e-mail. Praktische tips van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website marketing | Meer bezoekers & klanten | Noord',
      'og:description': 'Website marketing strategieën voor meer bezoekers en klanten. Van SEO en content marketing tot social media en e-mail.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-marketing',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2568" data-mp="1zxn130jqskmu">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2569" data-mp="1zzzqtzvqrzag">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2570" data-mp="13fhanxper341">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2571" data-mp="17gsshh21ik1o" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2572" data-mp="2ar6yayze3q0a" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2573" data-mp="25t553l892ab3" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2574" data-mp="1ru5ql6egbbsn">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2575" data-mp="1mwdqvq7keldw">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2576" data-mp="20s7mx8n8pg9l">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2577" data-mp="3c8fuxplfxjsn">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2578" data-mp="3cbbsvdxag8h3">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2579" data-mp="3fq3iv93cjvvw">/</span>
            <span className="text-accent-light font-medium" data-id="element-2580" data-mp="3kqrk0nlaht3u">
              Website marketing
            </span>
          </nav>
          <p className="text-accent-light font-semibold text-sm tracking-widest uppercase mb-4 animate-fade-in-up" data-id="element-2581" data-mp="35uijzjtkrbqd">
            Kennisbank
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up-delay-1" data-id="element-2582" data-mp="2cs35vksvjk1">
            Website marketing
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-2" data-id="element-2583" data-mp="7atic5f3uyq8">
            Hoe je meer bezoekers en klanten haalt uit je website met slimme
            online marketing.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-24 bg-white" data-id="element-2584" data-mp="l9tc1rhh63t9">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2585" data-mp="q1t46xrazsfa">
          <div className="prose prose-lg prose-slate max-w-none" data-id="element-2586" data-mp="q3e1uw8wzrhd">
            <h2 className="text-3xl font-bold text-navy-900 mb-6" data-id="element-2587" data-mp="2yk6ubn2df2eo">
              Wat is website marketing?
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-2588" data-mp="2tirafybxj1xv">
              Een mooie website is pas het begin. Zonder bezoekers levert zelfs
              de beste website geen klanten op. Website marketing (of online
              marketing) is het geheel van strategieën en technieken om gericht
              verkeer naar je website te trekken en deze bezoekers om te zetten
              in betalende klanten.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8" data-id="element-2589" data-mp="37jtz8iub6qch">
              Het doel is niet alleen méér bezoekers, maar vooral de júiste
              bezoekers. Mensen die daadwerkelijk op zoek zijn naar jouw
              diensten of producten.
            </p>

            <div className="bg-navy-50 p-8 rounded-2xl border border-navy-100 mb-12" data-id="element-2590" data-mp="3w1snj9oatia">
              <h3 className="text-xl font-bold text-navy-900 mb-4 flex items-center gap-2" data-id="element-2591" data-mp="3v7v3vgod9mmm">
                <TrendingUpIcon className="w-6 h-6 text-accent" data-id="element-2592" data-mp="7dzhvmi2qz6j" />
                Waarom is het belangrijk?
              </h3>
              <ul className="space-y-3" data-id="element-2593" data-mp="bgcd3gz73ep2">
                <li className="flex items-start gap-3" data-id="element-2594" data-mp="gctbr540tbge">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2595" data-mp="gffkv96k7z0q" />
                  <span className="text-slate-700" data-id="element-2596" data-mp="ldqq16lwtlcm">
                    Verhoogt je online zichtbaarheid in zoekmachines
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2597" data-mp="uc2hfaulon64">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2598" data-mp="z3u8rnlkywxb" />
                  <span className="text-slate-700" data-id="element-2599" data-mp="z66qbznff59u">
                    Bouwt vertrouwen en autoriteit op in jouw vakgebied
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2600" data-mp="193l5zx1izq13">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2601" data-mp="1e0lcg92aeusq" />
                  <span className="text-slate-700" data-id="element-2602" data-mp="ocpkkshq19hh">
                    Zorgt voor een continue stroom van nieuwe leads
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-2603" data-mp="jbrw5y64bj7j">
                  <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-2604" data-mp="y3u0pvj4v0dw" />
                  <span className="text-slate-700" data-id="element-2605" data-mp="tbsephw5xvao">
                    Meetbaar rendement op je investering (ROI)
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Strategies Section */}
      <section className="py-24 bg-surface-muted" data-id="element-2606" data-mp="1h1d1kbvftcp8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2607" data-mp="1h14oaivek5di">
          <div className="text-center mb-16" data-id="element-2608" data-mp="1c2a7fmgzwqw1">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2609" data-mp="1c3bme214txj1">
              Marketing strategieën
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-2610" data-mp="3du6h0qjlrqo6">
              De 4 belangrijkste pijlers om meer uit je website te halen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2611" data-mp="1tdyx88l5h63v">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2612" data-mp="1y9nrpxibbwtb">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2613" data-mp="1jljgtfpi4pl3">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-2614" data-mp="1ojb8q9q5cbpa" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2615" data-mp="1aklcewb5ig1z">
                SEO optimalisatie
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2616" data-mp="1eoaxvcwj4his">
                Zorg dat je website organisch gevonden wordt in Google. Door de
                juiste zoekwoorden te gebruiken en je website technisch te
                optimaliseren, trek je gratis relevant verkeer aan.
              </p>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2617" data-mp="2uzexwtzyv8ey">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2618" data-mp="1mtgcspesayke">
                <FileTextIcon className="w-6 h-6 text-accent" data-id="element-2619" data-mp="1is4n6jvwudc7" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2620" data-mp="1iu8qazlzj8bf">
                Content marketing
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2621" data-mp="192l1z7kq67e6">
                Trek bezoekers aan met waardevolle content. Blogs, kennisbank
                artikelen en cases laten je expertise zien en helpen potentiële
                klanten bij hun oriëntatieproces.
              </p>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2622" data-mp="v1s5ihav528f">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2623" data-mp="q3z1nux18kbb">
                <ShareIcon className="w-6 h-6 text-accent" data-id="element-2624" data-mp="2pf9um8qx275k" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2625" data-mp="2kgovydrbbvj8">
                Social media
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2626" data-mp="2rzpqwao1sguz">
                Vergroot je bereik via kanalen zoals LinkedIn, Instagram of
                Facebook. Deel je website content en stuur geïnteresseerd
                verkeer direct naar je landingspagina's.
              </p>
            </div>

            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-2627" data-mp="36olwm12mthsf">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-2628" data-mp="2936n80w0jl5t">
                <MailIcon className="w-6 h-6 text-accent" data-id="element-2629" data-mp="2e1hxgcq7zxok" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-2630" data-mp="2iyzxfdm9f2kf">
                E-mail marketing
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-2631" data-mp="2n37npf8d8qkr">
                Bouw een relatie op met je doelgroep. Verzamel e-mailadressen
                via je website en stuur gerichte nieuwsbrieven om top-of-mind te
                blijven bij je potentiële klanten.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-white border-b border-navy-100" data-id="element-2632" data-mp="f6scqjdrcoa5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2633" data-mp="jya1m92iayer">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-2634" data-mp="oxwqxgvm053x">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2635" data-mp="3nfjy4t1t1863">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2636" data-mp="3nhd19yd7la9c">
              Website laten maken
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2637" data-mp="67f0jiy91llq">•</span>
            <a href="#/website-tips" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2638" data-mp="68q4pjvlwvdd">
              Website tips
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2639" data-mp="1hoojm4dgubmi">•</span>
            <a href="#/kennisbank" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2640" data-mp="323b7otdvb77d">
              Kennisbank
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2641" data-mp="2sd8fuwihfzam">•</span>
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2642" data-mp="2ngi0yciez9qs">
              Homepage
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2643" data-mp="2ehefj1vryi94" />
    </main>;
}